import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seoGlobals from '../seo_globals.json';

const INDEXABLE_PREFIXES = ['/', '/latest', '/archive', '/highlight', '/highlights'];
const NON_INDEXABLE_PREFIXES = ['/vanityadmin', '/viewer', '/test-image', '/test-editor'];

const buildCanonical = (path: string) => {
    const baseUrl = seoGlobals.technical.baseUrl || 'https://vanity.co.at';
    if (path === '/') return baseUrl;
    return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
};

const isIndexablePath = (path: string) => {
    if (NON_INDEXABLE_PREFIXES.some(prefix => path.startsWith(prefix))) return false;
    return INDEXABLE_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
};

const ensureMetaTag = (name: string, content: string) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
};

const ensurePropertyTag = (property: string, content: string) => {
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
};

const ensureLinkTag = (rel: string, href: string, hreflang?: string) => {
    let selector = hreflang 
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]`;
    let tag = document.querySelector(selector);
    if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        if (hreflang) tag.setAttribute('hreflang', hreflang);
        document.head.appendChild(tag);
    }
    tag.setAttribute('href', href);
};

const SEOHead: React.FC = () => {
    const location = useLocation();

    useEffect(() => {
        const path = location.pathname || '/';
        const canonical = buildCanonical(path);
        const title = document.title || seoGlobals.seo.defaultTitle;
        const description = seoGlobals.seo.defaultDescription;
        const ogImage = seoGlobals.seo.defaultOgImage;

        document.title = title;
        ensureLinkTag('canonical', canonical);
        ensureMetaTag('description', description);

        const indexable = isIndexablePath(path);
        ensureMetaTag('robots', indexable ? 'index,follow' : 'noindex,nofollow');

        ensurePropertyTag('og:title', title);
        ensurePropertyTag('og:description', description);
        ensurePropertyTag('og:type', 'website');
        ensurePropertyTag('og:url', canonical);
        ensurePropertyTag('og:image', ogImage);
        ensurePropertyTag('og:site_name', seoGlobals.brand.legalName || seoGlobals.brand.name);

        ensurePropertyTag('twitter:card', seoGlobals.seo.twitterCard || 'summary_large_image');
        ensurePropertyTag('twitter:title', title);
        ensurePropertyTag('twitter:description', description);
        ensurePropertyTag('twitter:image', ogImage);

        // Geo-targeting meta tags
        ensureMetaTag('content-language', 'de-AT');
        ensurePropertyTag('og:locale', 'de_AT');
        ensurePropertyTag('og:locale:alternate', 'de');

        // Hreflang tags for German/Austrian targeting
        ensureLinkTag('alternate', canonical, 'de-AT');
        ensureLinkTag('alternate', canonical, 'de');
        ensureLinkTag('alternate', canonical, 'x-default');
    }, [location]);

    return null;
};

export default SEOHead;